import { RESUBMIT_INFO } from '../documents/docDependencies';
import { confirmPdfPages } from '../../lib/pdfPageGuard';
import { useRef, useState } from 'react';
import { isHealthDoc, openHealthMask } from '../../lib/healthMask';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;

export const DocumentUploadButton = ({ entityType = 'employees', entityId: employeeId, docKey, hasFile, onUploaded, autoApprove = false, label, noGlow = false, optional = false, attention = false, frame = false }) => {
  const alert = attention || frame || !hasFile;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("Dosya boyutu 10 MB'ı aşamaz");
      return;
    }
    if (isHealthDoc(entityType, docKey)) {
      // Sağlık raporu: dosya sunucuya gitmeden önce KVKK maskeleme ekranı açılır
      const info = await openHealthMask({ file, employeeId });
      if (info) {
        onUploaded(info);
        if (RESUBMIT_INFO[docKey]) toast.info(RESUBMIT_INFO[docKey], { duration: 8000 });
      }
      return;
    }
    if (!autoApprove && !(await confirmPdfPages(file, { collection: entityType, docKey }))) return;
    const body = new FormData();
    body.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/${entityType}/${employeeId}/document-files/${docKey}`, body, { params: autoApprove ? { approve: true } : {} });
      onUploaded(res.data);
      toast.success(autoApprove ? 'Belge yüklendi ve onaylandı' : 'Belge yüklendi');
      if (!autoApprove && RESUBMIT_INFO[docKey]) toast.info(RESUBMIT_INFO[docKey], { duration: 8000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={isHealthDoc(entityType, docKey) ? ".pdf,.jpg,.jpeg,.png" : ".pdf,.jpg,.jpeg,.png,.webp"} className="hidden" onChange={handleChange} data-testid={`doc-upload-input-${employeeId}-${docKey}`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`relative flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap rounded-md shadow-sm transition-colors disabled:opacity-50 ${
          attention ? 'bg-rose-600 text-white hover:bg-rose-700 upload-glow' : optional && !hasFile ? 'border-2 border-emerald-500 bg-white text-emerald-700 hover:bg-emerald-50' : !alert ? 'border border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100' : `border-2 border-rose-500 bg-white text-rose-600 hover:bg-rose-50 ${uploading || noGlow ? '' : 'upload-glow'}`
        }`}
        data-testid={`doc-upload-btn-${employeeId}-${docKey}`}
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {label || (hasFile ? 'Yeniden Yükle' : 'Belge Yükle')}
      </button>
    </>
  );
};
